import React, { useState, useEffect, useRef } from "react";
import { Layout, Modal, Input, Form, Dropdown, message, Button, List, Card } from "antd";
import {
	FolderOutlined,
	PlusOutlined,
	ExportOutlined,
} from "@ant-design/icons";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "../../App.css";
import type { MenuProps } from "antd";
import styled from "@emotion/styled";
import { authAxios } from "../utils/session_utils";
import { Bookmark, Item } from "./bookmark.types";
import FolderTree from "./FolderTree";
import BookmarkListItem from "./BookmarkListItem";
import { fetchBookmarksFromDB } from "./utils";

const { Content, Sider } = Layout;

const StyledLayout = styled(Layout)`
	min-height: 90vh;
	display: flex;
	flex-direction: row; //
`;

const StyledContent = styled(Content)`
	background-color: #fff !important;
	padding: 16px;
	justify-content: center;
	align-items: center;
	min-height: 90vh;
	display: flex;
	align-items: flex-start;
`;

const StyledCard = styled(Card)`
	width: 600px;
	max-height: 400px;
	overflow-y: auto;
	border-radius: 8px;
	// box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

	.ant-card-body {
		padding: 0;
	}
`;
const StyledSider = styled(Sider)`
	position: relative;
	background: #fff !important;
	border-right: 1px solid #2c2d2d29;
	min-height: 90vh;
`;
const ResizeHandle = styled.div`
	position: absolute;
	top: 0;
	right: -5px;
	width: 3px;
	height: 100%;
	cursor: ew-resize;
	background: transparent;
	z-index: 10;
	&:hover {
		background: black;
	}
`;


const BookmarkManager: React.FC = () => {
	const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
	const [selectedFolder, setSelectedFolder] = useState<string>("1");
	const [selectedItem, setSelectedItem] = useState<string | null>(null);
	const [expandedKeys, setExpandedKeys] = useState<string[]>(["1"]);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [modalType, setModalType] = useState<"bookmark" | "folder">("bookmark");
	const [editingItem, setEditingItem] = useState<Bookmark | null>(null);
	const [form] = Form.useForm();
	const contentRef = useRef<HTMLDivElement | null>(null);

	const loadBookmarks = async () => {
		const data = await fetchBookmarksFromDB();
		if (Array.isArray(data) && data.length > 0) {
			setBookmarks(data);
		}
	};

	useEffect(() => {
		loadBookmarks();
	}, []);

	const syncBookmarks = async (data: any) => {
		try {
			await authAxios({
				method: "post",
				url: `${process.env.REACT_APP_BACK_END_API}/bookmarks`,
				data: { bookmarks: data },
			});
		} catch (error) {
			console.error("Failed to sync bookmarks:", error);
		}
	};

	const findNode = (nodes: Bookmark[], id: string): Bookmark | null => {
		if (!Array.isArray(nodes)) return null;
		for (const node of nodes) {
			if (node.id === id) return node;
			if (Array.isArray(node.children) && node.children.length) {
				const found = findNode(node.children, id);
				if (found) return found;
			}
		}
		return null;
	};

	const removeNode = (nodes: Bookmark[], id: string): Bookmark[] => {
		if (!Array.isArray(nodes)) return [];
		return nodes
			.filter(node => node.id !== id)
			.map(node => ({
				...node,
				children: Array.isArray(node.children) ? removeNode(node.children, id) : [],
			}));
	};

	const addBookmark = (parentId: string, newItem: Bookmark) => {
		const addToTree = (nodes: Bookmark[]): Bookmark[] => {
			if (!Array.isArray(nodes)) return [];
			return nodes.map(node => {
				if (node.id === parentId) {
					return {
						...node,
						children: [...(Array.isArray(node.children) ? node.children : []), newItem],
					};
				}
				if (Array.isArray(node.children) && node.children.length) {
					return { ...node, children: addToTree(node.children) };
				}
				return node;
			});
		};
		setBookmarks(addToTree(bookmarks ?? []));
		syncBookmarks(addToTree(bookmarks ?? []));
	};

	const editBookmark = (id: string, updatedItem: Bookmark) => {
		const editInTree = (nodes: Bookmark[]): Bookmark[] => {
			if (!Array.isArray(nodes)) return [];
			return nodes.map(node => {
				if (node.id === id) {
					return { ...node, ...updatedItem };
				}
				if (Array.isArray(node.children) && node.children.length) {
					return { ...node, children: editInTree(node.children) };
				}
				return node;
			});
		};

		setBookmarks(editInTree(bookmarks ?? []));
		syncBookmarks(editInTree(bookmarks ?? []));
	};

	const deleteBookmark = (id: string) => {
		setBookmarks(removeNode(bookmarks ?? [], id));
		syncBookmarks(removeNode(bookmarks ?? [], id));
		if (selectedItem === id) setSelectedItem(null);
	};

	const handleSelect = (id: string, action?: string, item?: Bookmark) => {
		if (action === "bookmark" || action === "folder") {
			setModalType(action);
			setEditingItem(null);
			form.resetFields();
			setModalVisible(true);
		} else if (action === "edit") {
			setModalType(item!.type);
			setEditingItem(item!);
			form.setFieldsValue(item);
			setModalVisible(true);
		} else if (action === "delete") {
			deleteBookmark(id);
			message.success("Deleted successfully");
		} else if (action === "select") {
			setSelectedItem(id);
		} else {
			setSelectedFolder(id);
			setSelectedItem(null);
		}
	};

	const handleOk = () => {
		form.validateFields().then(values => {
			const newItem: Bookmark = {
				id: editingItem ? editingItem.id : Date.now().toString(),
				type: modalType,
				...values,
			};
			if (editingItem) {
				editBookmark(editingItem.id, newItem);
				message.success("Updated successfully");
			} else {
				addBookmark(selectedFolder, newItem);
				message.success("Added successfully");
			}
			setModalVisible(false);
			form.resetFields();
		});
	};

	const handleDrop = (
		draggedItem: Item,
		targetId: string,
		position: "above" | "below" | "inside"
	) => {
		if (draggedItem.id === targetId) {
			message.error("Cannot move item to itself");
			return;
		}

		const dragNode = findNode(bookmarks ?? [], draggedItem.id);
		if (!dragNode) {
			message.error("Dragged item not found");
			return;
		}

		const targetNode = findNode(bookmarks, targetId);
		if (!targetNode) {
			message.error("Target item not found");
			return;
		}

		if (draggedItem.type === "folder" && targetNode.type === "folder" && position === "inside") {
			const isDescendant = (node: Bookmark, id: string): boolean => {
				if (!Array.isArray(node.children)) return false;
				return node.children.some(child => child.id === id || isDescendant(child, id));
			};
			if (isDescendant(dragNode, targetId)) {
				message.error("Cannot move a folder into itself");
				return;
			}
		}

		let newBookmarks = JSON.parse(JSON.stringify(bookmarks)) as Bookmark[];
		newBookmarks = removeNode(newBookmarks, draggedItem.id);

		const insertNode = (
			nodes: Bookmark[],
			targetKey: string,
			newNode: Bookmark,
			pos: "above" | "below" | "inside"
		): Bookmark[] => {
			if (!Array.isArray(nodes)) return [];
			const parent = findParent(nodes, targetKey);
			if (parent) {
				return nodes.map(node => {
					if (node.id === parent.id) {
						const targetIndex = node.children!.findIndex(child => child.id === targetKey);
						if (targetIndex === -1) return node;
						const newChildren = [...(Array.isArray(node.children) ? node.children : [])];
						if (pos === "inside" && newChildren[targetIndex].type === "folder") {
							newChildren[targetIndex] = {
								...newChildren[targetIndex],
								children: [...(newChildren[targetIndex].children ?? []), newNode],
							};
						} else {
							newChildren.splice(pos === "above" ? targetIndex : targetIndex + 1, 0, newNode);
						}
						return { ...node, children: newChildren };
					}
					if (Array.isArray(node.children) && node.children.length) {
						return { ...node, children: insertNode(node.children, targetKey, newNode, pos) };
					}
					return node;
				});
			} else {
				const targetIndex = nodes.findIndex(node => node.id === targetKey);
				if (targetIndex === -1) return nodes;
				if (pos === "inside" && nodes[targetIndex].type === "folder") {
					return nodes.map((node, index) => {
						if (index === targetIndex) {
							return {
								...node,
								children: [...(node.children ?? []), newNode],
							};
						}
						return node;
					});
				} else {
					const newNodes = [...nodes];
					newNodes.splice(pos === "above" ? targetIndex : targetIndex + 1, 0, newNode);
					return newNodes;
				}
			}
		};

		const findParent = (
			nodes: Bookmark[],
			key: string,
			parent: Bookmark | null = null
		): Bookmark | null => {
			if (!Array.isArray(nodes)) return null;
			for (const node of nodes) {
				if (node.id === key) return parent;
				if (Array.isArray(node.children) && node.children.length) {
					const found = findParent(node.children, key, node);
					if (found) return found;
				}
			}
			return null;
		};

		newBookmarks = insertNode(newBookmarks, targetId, dragNode, position);
		setBookmarks(newBookmarks);
		syncBookmarks(newBookmarks);
	};

	const currentFolder = findNode(bookmarks, selectedFolder) || {
		children: [],
		title: "Bookmarks Menu",
	};

	const contentContextMenu: MenuProps = {
		items: [
			{
				key: "new-bookmark",
				label: "New bookmark",
				icon: <ExportOutlined />,
				onClick: () => handleSelect(selectedFolder, "bookmark"),
			},
			{
				key: "new-folder",
				label: "New folder",
				icon: <FolderOutlined />,
				onClick: () => handleSelect(selectedFolder, "folder"),
			},
		],
	};

	const [siderWidth, setSiderWidth] = useState(300); // Initial width
	const minWidth = 200; // Minimum sider width
	const maxWidth = 600; // Maximum sider width
	const siderRef = useRef(null);
	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		const startX = e.clientX;
		const startWidth = siderWidth;

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const newWidth = startWidth + (moveEvent.clientX - startX);
			if (newWidth >= minWidth && newWidth <= maxWidth) {
				setSiderWidth(newWidth);
			}
		};

		const handleMouseUp = () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	};
	return (
		<DndProvider backend={HTML5Backend}>
			<StyledLayout>
				<StyledSider width={siderWidth} ref={siderRef}>
					<div
						style={{
							padding: "16px",
							overflowX: "auto",
							whiteSpace: "nowrap",
							height: "inherit",
						}}
					>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => handleSelect(selectedFolder, "bookmark")}
							style={{
								width: "100%",
								fontFamily: "Roboto, Arial, sans-serif",
								fontSize: "14px",
							}}
						>
							New bookmark
						</Button>
						<div style={{ marginTop: "8px" }}>
							<FolderTree
								data={bookmarks}
								onSelect={handleSelect}
								onDrop={handleDrop}
								expandedKeys={expandedKeys}
								setExpandedKeys={setExpandedKeys}
								selectedFolder={selectedFolder}
							/>
						</div>
					</div>
					<ResizeHandle onMouseDown={handleMouseDown} />
				</StyledSider>
				<StyledContent
					ref={contentRef}
					onContextMenu={e => {
						if (!e.target || !(e.target as HTMLElement).closest(".ant-list-item")) {
							e.preventDefault();
						}
					}}
				>
					<Dropdown menu={contentContextMenu} trigger={["contextMenu"]}>
						<div
							style={{
								width: "100%",
								height: "100%",
								display: "flex",
								justifyContent: "center",
								padding: "0 0 16px 0",
							}}
						>
							<div style={{ width: "100%", maxWidth: "600px" }}>
								<StyledCard title={currentFolder.title}>
									<List
										dataSource={currentFolder.children || []}
										renderItem={(item: Bookmark) => (
											<BookmarkListItem
												item={item}
												handleSelect={handleSelect}
												onDrop={handleDrop}
												isSelected={selectedItem === item.id}
											/>
										)}
										locale={{
											emptyText: (
												<div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
													📭 No bookmarks in this folder.
												</div>
											),
										}}
									/>
								</StyledCard>
							</div>
						</div>
					</Dropdown>
				</StyledContent>
				<Modal
					title={
						modalType === "bookmark"
							? editingItem
								? "Edit bookmark"
								: "New bookmark"
							: editingItem
							? "Rename folder"
							: "New folder"
					}
					open={modalVisible}
					centered
					onOk={handleOk}
					onCancel={() => setModalVisible(false)}
					okText="Save"
					cancelText="Cancel"
					style={{ fontFamily: "Roboto, Arial, sans-serif" }}
				>
					<Form form={form} layout="vertical" requiredMark={false}>
						<Form.Item
							name="title"
							label="Name"
							rules={[{ required: true, message: "Please enter a name" }]}
						>
							<Input style={{ fontFamily: "Roboto, Arial, sans-serif", fontSize: "14px" }} />
						</Form.Item>
						{modalType === "bookmark" && (
							<Form.Item
								name="url"
								label="URL"
								rules={[
									{ required: true, message: "Please enter a URL" },
									{ type: "url", message: "Please enter a valid URL" },
								]}
							>
								<Input style={{ fontFamily: "Roboto, Arial, sans-serif", fontSize: "14px" }} />
							</Form.Item>
						)}
					</Form>
				</Modal>
			</StyledLayout>
		</DndProvider>
	);
};

export default BookmarkManager;
